import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Banner, Btn, Stepper, Tag } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { QuizCard, scoreQuiz, PASS_THRESHOLD } from "../components/Quiz.js";
import { StepMedia, StepText } from "../components/StepBody.js";
import { PrintSheet } from "../components/PrintSheet.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, mediaUrl, stores as storesApi, type QuizQuestion, type QuizResult } from "../lib/api.js";
import { type StepWithCandidates } from "../lib/demo.js";
import {
  getRead,
  markRead,
  recordQuiz,
  isComplete,
  getViewMode,
  setViewMode,
  type ReaderViewMode,
} from "../lib/readingProgress.js";

function reduceMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion:reduce)").matches;
}

type View = "cover" | "read" | "quiz";

/**
 * 閲覧者リーダー /read/:id — distraction-free reading (field/dark), no creator tools.
 * DEFAULT layout = "マニュアル" (continuous document, fast to scan/review); a remembered toggle
 * switches to "1手順ずつ" (paged/focus, good for hands-busy first-read). STILL-FIRST media: each
 * step shows its lightweight keyframe JPEG; the heavy original video is loaded only on an explicit
 * "▶動画" tap (one inline <video>, disposed on change). End-of-book comprehension quiz (80% = 修了).
 */
export function Reader() {
  useSurface("console");
  const navigate = useNavigate();
  const location = useLocation();
  const { id = "" } = useParams();
  const [sp] = useSearchParams();
  // 来た場所（本 /book/:id・ダッシュボード等）へ戻る。直リンクは書架へ。
  const backTo = (location.state as { from?: string } | null)?.from ?? "/library";
  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);

  const steps = (data?.steps ?? []) as StepWithCandidates[];
  const total = steps.length;

  const initialMode: ReaderViewMode = (() => {
    const p = sp.get("view");
    if (p === "doc" || p === "paged") return p;
    return getViewMode();
  })();
  const [viewMode, setMode] = useState<ReaderViewMode>(initialMode);
  const [view, setView] = useState<View>(() => {
    if (sp.get("quiz") === "1") return "quiz";
    if (Number(sp.get("step")) >= 1) return "read";
    return initialMode === "doc" ? "read" : "cover";
  });

  const [idx, setIdx] = useState(0); // paged current step
  const [activeSeq, setActiveSeq] = useState(1); // doc most-visible step (1-based)
  const shellRef = useRef<HTMLDivElement>(null);
  const appbarRef = useRef<HTMLElement>(null);

  // Expose the (wrap-dependent) appbar height so the doc-mode sticky nav pins right below it
  // instead of sliding under the translucent bar.
  useEffect(() => {
    const bar = appbarRef.current;
    const shell = shellRef.current;
    if (!bar || !shell) return;
    const set = () => shell.style.setProperty("--appbar-h", `${bar.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [data]); // refs mount only after loading/error frames are replaced by the real shell
  const [dir, setDir] = useState<"next" | "prev">("next");
  const [openVideoStepId, setOpenVideoStepId] = useState<string | null>(null);
  const [wantScrollSeq, setWantScrollSeq] = useState<number | null>(null);
  const [speaking, setSpeaking] = useState(false);
  const [handsfree, setHandsfree] = useState(false);
  const handsfreeRef = useRef(handsfree);
  useEffect(() => { handsfreeRef.current = handsfree; }, [handsfree]);

  const videoSrc = useMemo(
    () => (data?.sourceType === "video" ? mediaUrl(data.media.originalVideoUrl) : null),
    [data?.sourceType, data?.media.originalVideoUrl],
  );

  // Resolve the store NAME for the printed document header (best-effort).
  const [storeName, setStoreName] = useState<string | null>(null);
  useEffect(() => {
    const sid = data?.meta.storeId;
    if (!sid) { setStoreName(null); return; }
    void storesApi.list().then((ss) => setStoreName(ss.find((s) => s.id === sid)?.name ?? null)).catch(() => {});
  }, [data?.meta.storeId]);

  // Seed position from ?step or the stored "続きから" once the manual loads.
  useEffect(() => {
    if (!data) return;
    const sParam = Number(sp.get("step"));
    let start = 0;
    if (Number.isFinite(sParam) && sParam >= 1 && sParam <= total) start = sParam - 1;
    else {
      const r = getRead(id);
      if (r && r.lastStep > 0 && r.lastStep < total) start = r.lastStep;
    }
    setIdx(start);
    setActiveSeq(start + 1);
    if (viewMode === "doc" && start > 0) setWantScrollSeq(start + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const step = steps[idx];

  function toggleVideo(stepId: string) {
    setOpenVideoStepId((p) => (p === stepId ? null : stepId));
  }

  // --- paged navigation ---
  function go(delta: number) {
    setDir(delta > 0 ? "next" : "prev");
    setOpenVideoStepId(null);
    setView("read");
    setIdx((i) => Math.min(total - 1, Math.max(0, i + delta)));
  }
  function next() {
    if (idx >= total - 1) {
      window.speechSynthesis?.cancel();
      setSpeaking(false);
      setView("quiz");
      return;
    }
    go(1);
  }
  function prev() {
    if (idx <= 0) {
      setView("cover");
      return;
    }
    go(-1);
  }

  // paged keyboard
  useEffect(() => {
    if (view !== "read" || viewMode !== "paged") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "End") setIdx(total - 1);
      else if (e.key === "Home") setIdx(0);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, viewMode, idx, total]);

  // paged: persist progress on step view
  useEffect(() => {
    if (view === "read" && viewMode === "paged" && total > 0) markRead(id, idx, total);
  }, [view, viewMode, idx, total, id]);

  // paged touch swipe (local, axis-locked — reader is not inside ConsoleShell)
  const touch = useRef<{ x: number; y: number } | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent) {
    const s = touch.current;
    touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) (dx < 0 ? next : prev)();
  }

  // --- document mode: IntersectionObserver → active step + progress ---
  useEffect(() => {
    if (view !== "read" || viewMode !== "doc" || !total) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>("article.sline[data-seq]"));
    if (!els.length) return;
    const ratios = new Map<number, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) ratios.set(Number((e.target as HTMLElement).dataset.seq), e.intersectionRatio);
        let best = 0;
        let bestSeq = 0;
        ratios.forEach((r, seq) => {
          if (r > best) { best = r; bestSeq = seq; }
        });
        if (bestSeq) {
          setActiveSeq(bestSeq);
          markRead(id, bestSeq - 1, total);
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [view, viewMode, total, id]);

  // document mode: scroll to a requested step (続きから / mode switch)
  useEffect(() => {
    if (view === "read" && viewMode === "doc" && wantScrollSeq) {
      const el = document.getElementById(`step-${wantScrollSeq}`);
      el?.scrollIntoView({ block: "start", behavior: reduceMotion() ? "auto" : "smooth" });
      setWantScrollSeq(null);
    }
  }, [view, viewMode, wantScrollSeq]);

  // --- TTS ---
  function speakUtter(text: string, onend: () => void) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = data?.locale ?? "ja-JP";
    u.onend = onend;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  }
  function speakPaged(s: StepWithCandidates | undefined) {
    if (!("speechSynthesis" in window) || !s) return;
    speakUtter(`${s.title}。${s.caption}`, () => {
      setSpeaking(false);
      if (handsfreeRef.current) window.setTimeout(() => next(), 400);
    });
  }
  function speakDoc(seq: number) {
    if (!("speechSynthesis" in window)) return;
    const s = steps[seq - 1];
    if (!s) { setSpeaking(false); return; }
    document.getElementById(`step-${seq}`)?.scrollIntoView({ block: "start", behavior: reduceMotion() ? "auto" : "smooth" });
    setActiveSeq(seq);
    markRead(id, seq - 1, total);
    speakUtter(`${s.title}。${s.caption}`, () => {
      if (handsfreeRef.current && seq < total) speakDoc(seq + 1);
      else setSpeaking(false);
    });
  }
  function toggleTts() {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSpeaking((s) => !s);
      return;
    }
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    if (viewMode === "doc") speakDoc(activeSeq);
    else speakPaged(step);
  }
  // paged hands-free auto-read on step change
  useEffect(() => {
    if (view === "read" && viewMode === "paged" && handsfree) speakPaged(step);
    return () => window.speechSynthesis?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, view, viewMode, handsfree]);
  // doc hands-free: begin sequential read when toggled on
  useEffect(() => {
    if (view === "read" && viewMode === "doc" && handsfree && !speaking) speakDoc(activeSeq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handsfree]);
  // stop speech on unmount
  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  function changeMode(m: ReaderViewMode) {
    if (m === viewMode) return;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setOpenVideoStepId(null);
    setMode(m);
    setViewMode(m);
    if (view === "cover") { if (m === "doc") setView("read"); return; }
    if (m === "paged") setIdx(Math.max(0, activeSeq - 1));
    else setWantScrollSeq(idx + 1); // → doc: scroll to current step
  }

  /** Print / Save-as-PDF: the hidden <PrintSheet> renders the manual as a FORMAL document; preload
   *  its (lazy) keyframe images, then open the print dialog (the print stylesheet shows only it). */
  function printDoc() {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    const imgs = Array.from(document.querySelectorAll<HTMLImageElement>(".printsheet img"));
    // Force-load + warm the cache so the print snapshot has every image.
    Promise.all(
      imgs.map((i) => {
        i.loading = "eager";
        const src = i.getAttribute("src");
        if (!src || i.complete) return Promise.resolve();
        return new Promise((res) => {
          const pre = new Image();
          pre.onload = pre.onerror = () => res(null);
          pre.src = src;
        });
      }),
    ).then(() => window.setTimeout(() => window.print(), 120));
  }

  const resume = useMemo(() => {
    const r = getRead(id);
    return r && r.lastStep > 0 && r.lastStep < total ? r.lastStep : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, total, data]);

  // ---------------- render ----------------
  if (loading) return <ReaderFrame><LoadingState /></ReaderFrame>;
  if (error) return <ReaderFrame><ErrorState message={error} offline={offline} onRetry={reload} /></ReaderFrame>;
  if (!data) return <ReaderFrame><EmptyState title="マニュアルが見つかりません" /></ReaderFrame>;

  const reading = view === "read";
  // 修了 = 読了100% AND クイズ合格 — 本棚のリング・学習管理と同じ定義（クイズ合格だけで
  // 修了と表示すると、管理側の記録と本人の見た目が食い違う）。
  const completedManual = isComplete(id);
  const mins = data.meta.estimatedTotalMinutes;

  return (
    <div className="shell" data-surface="console" ref={shellRef}>
      <header className="appbar" ref={appbarRef}>
        <button
          className="btn btn--ghost"
          aria-label="閉じる"
          onClick={() => navigate(backTo)}
          style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12 }}
        >
          <Icon name="close" size={20} />
        </button>
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="kicker" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data.title}</div>
          {reading && total ? (
            viewMode === "paged" ? (
              <Stepper total={total} current={idx + 1} />
            ) : (
              <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>STEP {String(activeSeq).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
            )
          ) : null}
        </div>

        {view !== "quiz" ? (
          <div role="radiogroup" aria-label="表示モード" style={pillStyle}>
            {([["doc", "マニュアル"], ["paged", "1手順ずつ"]] as [ReaderViewMode, string][]).map(([m, label]) => (
              <button key={m} role="radio" aria-checked={viewMode === m} onClick={() => changeMode(m)} style={modeBtn(viewMode === m)}>
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {reading ? (
          <>
            <button className="chip" onClick={() => setView("quiz")} style={chipStyle} title="理解度クイズを受ける">
              <Icon name="ai-review" size={16} /> クイズ
            </button>
            <button className="chip" onClick={printDoc} style={chipStyle} title="PDF出力・印刷">
              <Icon name="print" size={16} /> PDF
            </button>
            <button className="chip" aria-pressed={handsfree} onClick={() => setHandsfree((h) => !h)} style={chipStyle} title="読み上げて自動でめくる/送る">
              <Icon name={handsfree ? "pause" : "play"} size={16} /> ハンズフリー
            </button>
            <button className="chip" aria-pressed={speaking} onClick={toggleTts} style={chipStyle}>
              {speaking ? <Icon name="pause" size={16} /> : <Icon name="speaker" size={16} />}
              {speaking ? "読み上げ中" : "読み上げ"}
            </button>
          </>
        ) : null}
        <span className="chip" style={{ ...chipStyle, padding: 8 }}>
          <Icon name="multilingual" size={16} />
          {(data.locale ?? "ja").toUpperCase()}
        </span>
      </header>

      {view === "cover" ? (
        <ReaderCover
          manual={data}
          total={total}
          resumeStep={resume}
          onStart={() => { setIdx(0); setView("read"); }}
          onResume={(s) => { setIdx(s); setView("read"); }}
          onQuiz={() => setView("quiz")}
        />
      ) : view === "quiz" ? (
        <ReaderQuiz
          manualId={id}
          version={data.version}
          onBackToStep={(seq) => { setIdx(Math.min(total - 1, Math.max(0, seq - 1))); setActiveSeq(seq); setView("read"); if (viewMode === "doc") setWantScrollSeq(seq); }}
          onRestart={() => { setIdx(0); setView("read"); }}
          onClose={() => navigate(backTo)}
        />
      ) : viewMode === "doc" ? (
        // ---------- DOCUMENT (manual format) — a paper sheet laid on the ruled desk ----------
        <main className="rdoc crop">
          <style>{docCSS}</style>
          <div className="rdoc-band" aria-hidden="true">
            <span>業務マニュアル / OPERATING MANUAL</span>
            <span>v{data.version ?? 1}</span>
          </div>
          <div className="rdoc-head">
            <div className="kicker">{data.meta.category ?? "未分類"} · 全{total}手順</div>
            <h1 className="display jp" style={{ fontSize: "clamp(26px,5vw,42px)", lineHeight: 1.06, letterSpacing: "-.02em", margin: "8px 0 12px" }}>
              {data.title}
            </h1>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <Tag mono>{total} 手順</Tag>
              {mins ? <Tag mono>所要 {mins}分</Tag> : null}
              <Tag mono>{(data.locale ?? "ja").toUpperCase()}</Tag>
              {completedManual ? <Tag dot color="var(--ok)">修了</Tag> : null}
            </div>
          </div>

          <nav className="docnav" aria-label="手順の一覧">
            {steps.map((s) => (
              <a key={s.stepId} href={`#step-${s.order}`} aria-current={activeSeq === s.order ? "step" : undefined} aria-label={`手順${s.order}へ`}>
                {String(s.order).padStart(2, "0")}
              </a>
            ))}
          </nav>

          {steps.map((s, i) => (
            <article key={s.stepId} className="sline" id={`step-${s.order}`} data-seq={s.order} aria-label={`手順 ${s.order} / ${total}`}>
              <div className="n" aria-hidden="true">{String(s.order).padStart(2, "0")}</div>
              <div className="sline-body">
                <h2 className="t-lg jp" style={{ margin: "2px 0 10px" }}>{s.title}</h2>
                <StepMedia step={s} variant="doc" videoSrc={videoSrc} videoOpen={openVideoStepId === s.stepId} onToggleVideo={() => toggleVideo(s.stepId)} eager={i === 0} />
                <StepText step={s} />
              </div>
            </article>
          ))}

          <div className="rdoc-foot" style={{ textAlign: "center", marginTop: 34 }}>
            <div className="row" style={{ justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <Btn variant="hi" lg onClick={() => setView("quiz")} icon={<Icon name="ai-review" size={18} />}>理解度クイズに進む</Btn>
              <Btn variant="ghost" lg onClick={printDoc} icon={<Icon name="print" size={18} />}>PDF・印刷</Btn>
            </div>
            <div style={{ marginTop: 12 }}>
              <Btn variant="ghost" onClick={() => navigate(backTo)} icon={<Icon name="arrow-left" size={16} />}>書架に戻る</Btn>
            </div>
          </div>

          <div className="rdoc-docfoot" aria-hidden="true">
            <span>TEBIKI · {data.title}</span>
            <span>全{total}手順 · v{data.version ?? 1}</span>
          </div>
        </main>
      ) : step ? (
        // ---------- PAGED (1手順ずつ) ----------
        <main className="view-main" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} aria-label={`手順 ${idx + 1} / ${total}`}>
          <div
            className={`stage-img${reduceMotion() ? "" : dir === "next" ? " pageturn--next" : " pageturn--prev"}`}
            key={`stage-${idx}`}
            style={{ position: "relative", background: "radial-gradient(120% 90% at 50% 20%, var(--bg2), var(--bg))", display: "grid", placeItems: "center", padding: 30 }}
          >
            <StepMedia step={step} variant="paged" videoSrc={videoSrc} videoOpen={openVideoStepId === step.stepId} onToggleVideo={() => toggleVideo(step.stepId)} eager />
            {openVideoStepId !== step.stepId ? (
              <>
                <button className="tapzone tapzone--l" aria-label="前の手順" onClick={prev} />
                <button className="tapzone tapzone--r" aria-label="次の手順" onClick={next} />
              </>
            ) : null}
            <span className="statuspill" style={{ position: "absolute", bottom: 18, right: 18, display: "inline-flex", alignItems: "center", gap: 5, pointerEvents: "none" }}>
              <span style={{ display: "inline-block", width: 16, height: 7, borderRadius: 4, background: "var(--hi)", opacity: 0.85 }} aria-hidden="true" />
              公式マーク
            </span>
          </div>

          <div className={`read${reduceMotion() ? "" : dir === "next" ? " pageturn--next" : " pageturn--prev"}`} key={`read-${idx}`}>
            <div className="read-head">
              <span className="read-num" aria-hidden="true">{String(step.order).padStart(2, "0")}</span>
              <div style={{ minWidth: 0 }}>
                <div className="kicker">STEP {String(step.order).padStart(2, "0")} / {String(total).padStart(2, "0")}</div>
                <h1 className="jp display read-title">{step.title}</h1>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <StepText step={step} bodyStyle={{ fontSize: 18 }} />
            </div>
            <div className="navbtns" style={{ display: "flex", gap: 12, marginTop: "auto", paddingTop: 22 }}>
              <Btn variant="ghost" lg className="grow" onClick={prev} icon={<Icon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />}>
                {idx === 0 ? "表紙" : "前の手順"}
              </Btn>
              <Btn variant="hi" lg className="grow" onClick={next}>
                {idx < total - 1 ? "次の手順" : "理解度クイズに進む"}
                <Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)", marginLeft: 6, verticalAlign: "middle" }} />
              </Btn>
            </div>
          </div>
        </main>
      ) : (
        <ReaderFrame><EmptyState title="この手順は表示できません" /></ReaderFrame>
      )}

      {/* Formal printable manual document — hidden on screen, shown only in @media print. */}
      <PrintSheet manual={data} storeName={storeName} />
    </div>
  );
}

const chipStyle: React.CSSProperties = { minHeight: 48, display: "inline-flex", alignItems: "center", gap: 6 };
const pillStyle: React.CSSProperties = { display: "inline-flex", gap: 3, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 999, padding: 3 };
function modeBtn(active: boolean): React.CSSProperties {
  return {
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    border: 0,
    cursor: "pointer",
    minHeight: 44,
    background: active ? "var(--ink)" : "transparent",
    color: active ? "var(--bg)" : "var(--muted)",
    transition: "background .15s, color .15s",
  };
}

/* The document rides on a PAPER SHEET (PrintSheet-like margins/shadow) so the ruled desk
   background never sits under body text. The sheet = surface + hairline frame + ink top rule +
   トンボ (via .crop) + document-control band/footer — a formal printed manual, on screen. */
const docCSS = `
.rdoc{--pad:clamp(20px,5vw,56px);position:relative;
  width:min(880px,calc(100% - clamp(20px,5vw,52px)));margin:clamp(14px,3.5vw,36px) auto 96px;
  padding:0 var(--pad) clamp(30px,4vw,46px);
  background:var(--surface);border:1px solid var(--line);border-top:3px solid var(--ink);
  border-radius:2px;box-shadow:var(--shadow-2)}
.rdoc-band{display:flex;justify-content:space-between;align-items:baseline;gap:14px;padding:13px 0 10px;
  border-bottom:1px solid var(--line);font-family:var(--font-mono);font-size:10px;letter-spacing:.18em;
  text-transform:uppercase;color:var(--faint);white-space:nowrap}
.rdoc-band span{overflow:hidden;text-overflow:ellipsis}
.rdoc-head{padding:clamp(16px,3.5vw,26px) 0 16px;border-bottom:2px solid var(--ink)}
.docnav{position:sticky;top:var(--appbar-h,0px);z-index:6;display:flex;gap:6px;overflow-x:auto;
  margin:0 calc(-1*var(--pad));padding:10px var(--pad);
  background:color-mix(in srgb,var(--surface) 92%,transparent);backdrop-filter:blur(8px);
  border-bottom:1px solid var(--line);scrollbar-width:none}
.docnav::-webkit-scrollbar{display:none}
.docnav a{flex:0 0 auto;font-family:var(--font-mono);font-size:12px;min-width:36px;height:34px;display:grid;place-items:center;
  border-radius:8px;border:1px solid var(--line);color:var(--muted);scroll-snap-align:start}
.docnav a[aria-current="step"]{background:var(--ink);color:var(--bg);border-color:var(--ink)}
.rdoc .sline{position:relative;display:grid;grid-template-columns:auto 1fr;gap:20px;padding:26px 0;border-bottom:1px dashed var(--line-2);
  content-visibility:auto;contain-intrinsic-size:0 380px;scroll-margin-top:calc(var(--appbar-h,0px) + 60px)}
.rdoc .sline:last-of-type{border-bottom:0}
.rdoc .sline .n{font-family:var(--font-display);font-weight:500;font-size:clamp(34px,6vw,60px);line-height:.8;letter-spacing:-.04em;
  color:transparent;-webkit-text-stroke:1.4px var(--line-2)}
.rdoc .sline-body{min-width:0}
.rdoc .sline-body::after{content:"";display:block;clear:both}
.rdoc .tbl{max-width:100%}
.rdoc-docfoot{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:30px;padding-top:12px;
  border-top:1px solid var(--line);font-family:var(--font-mono);font-size:10px;letter-spacing:.16em;
  text-transform:uppercase;color:var(--faint)}
.rdoc-docfoot span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%}
@media(max-width:680px){ .rdoc .sline{grid-template-columns:1fr;gap:6px} .rdoc .sline .n{font-size:30px} }
`;

/** Minimal field-surface frame for loading/error/empty states. */
function ReaderFrame({ children }: { children: React.ReactNode }) {
  useSurface("console");
  return (
    <div className="shell" data-surface="console">
      <main className="wrap" style={{ paddingTop: 40 }}>{children}</main>
    </div>
  );
}

// ---------------- cover spread (paged only) ----------------

function ReaderCover({
  manual,
  total,
  resumeStep,
  onStart,
  onResume,
  onQuiz,
}: {
  manual: import("@tebiki/shared").Manual;
  total: number;
  resumeStep: number | null;
  onStart: () => void;
  onResume: (step: number) => void;
  onQuiz: () => void;
}) {
  const mins = manual.meta.estimatedTotalMinutes;
  return (
    <main className="read-cover">
      <div className="num num--outline" aria-hidden="true" style={{ margin: "0 auto 6px" }}>
        {String(total).padStart(2, "0")}
      </div>
      <div className="kicker" style={{ justifyContent: "center" }}>{manual.meta.category ?? "未分類"} · 全{total}手順</div>
      <h1 className="display jp" style={{ fontSize: "clamp(30px,7vw,56px)", lineHeight: 1.04, letterSpacing: "-.02em", margin: "12px 0 18px" }}>
        {manual.title}
      </h1>
      <div className="row" style={{ justifyContent: "center", flexWrap: "wrap", gap: 8, marginBottom: 26 }}>
        <Tag mono>{total} 手順</Tag>
        {mins ? <Tag mono>所要 {mins}分</Tag> : null}
        <Tag mono>{(manual.locale ?? "ja").toUpperCase()}</Tag>
        {isComplete(manual.manualId) ? <Tag dot color="var(--ok)">修了</Tag> : null}
      </div>
      <div className="row" style={{ justifyContent: "center", flexWrap: "wrap", gap: 12 }}>
        <Btn variant="hi" lg onClick={onStart} icon={<Icon name="play" size={18} />}>
          {resumeStep != null ? "最初から読む" : "読みはじめる"}
        </Btn>
        {resumeStep != null ? (
          <Btn variant="ghost" lg onClick={() => onResume(resumeStep)}>続きから（手順{resumeStep + 1}）</Btn>
        ) : null}
        <Btn variant="ghost" lg onClick={onQuiz} icon={<Icon name="ai-review" size={18} />}>理解度クイズ</Btn>
      </div>
    </main>
  );
}

// ---------------- end-of-book quiz ----------------

function ReaderQuiz({
  manualId,
  version,
  onBackToStep,
  onRestart,
  onClose,
}: {
  manualId: string;
  version: number;
  onBackToStep: (stepSeq: number) => void;
  onRestart: () => void;
  onClose: () => void;
}) {
  const [questions, setQuestions] = useState<QuizQuestion[] | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [qi, setQi] = useState(0);
  const [phase, setPhase] = useState<"loading" | "quiz" | "result" | "error" | "empty">("loading");

  async function generate() {
    setPhase("loading");
    setQuestions(null);
    setAnswers({});
    setQi(0);
    try {
      const res = await manuals.quiz(manualId);
      if (!res.questions.length) { setPhase("empty"); return; }
      setQuestions(res.questions);
      setPhase("quiz");
    } catch {
      setPhase("error");
    }
  }
  useEffect(() => { void generate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [manualId]);

  const result: QuizResult | null = useMemo(
    () => (questions ? scoreQuiz(questions, answers) : null),
    [questions, answers],
  );

  function answer(ci: number) {
    setAnswers((prev) => (qi in prev ? prev : { ...prev, [qi]: ci }));
  }
  function finish() {
    if (!questions || !result) return;
    recordQuiz(manualId, result.score, result.passed, version);
    setPhase("result");
  }

  if (phase === "loading") return <ReaderFrame><LoadingState label="AIがクイズを生成中…" /></ReaderFrame>;
  if (phase === "error")
    return (
      <ReaderFrame>
        <EmptyState icon={<Icon name="warning" size={30} />} title="クイズを生成できませんでした">
          通信環境を確認してください。
          <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn onClick={() => void generate()} icon={<Icon name="redo-cycle" size={16} />}>再試行</Btn>
            <Btn variant="ghost" onClick={onClose}>書架に戻る</Btn>
          </div>
        </EmptyState>
      </ReaderFrame>
    );
  if (phase === "empty")
    return (
      <ReaderFrame>
        <EmptyState title="このマニュアルのクイズはまだ作れません">
          手順が十分にない可能性があります。
          <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "center" }}>
            <Btn variant="hi" onClick={onClose}>書架に戻る</Btn>
          </div>
        </EmptyState>
      </ReaderFrame>
    );

  if (phase === "result" && result) {
    const passed = result.passed;
    const uniqWrong = [
      ...new Set(
        (questions ?? [])
          .map((q, i) => ({ q, i }))
          .filter(({ q, i }) => answers[i] !== q.answerIndex && q.stepSeq != null)
          .map(({ q }) => q.stepSeq as number),
      ),
    ];
    return (
      <main className="wrap" style={{ paddingTop: 40, paddingBottom: 60, maxWidth: 640 }}>
        <div className={`crop${passed && !reduceMotion() ? " celebrate" : ""}`} style={{ textAlign: "center", padding: "34px 24px", border: "1px solid var(--line)", borderRadius: 16, background: "var(--surface)", position: "relative" }} role="status" aria-live="polite">
          <div className="num num--outline" style={{ margin: "0 auto 4px", color: "transparent", WebkitTextStroke: `2px ${passed ? "var(--hi)" : "var(--line-2)"}` }}>
            {result.score}<span style={{ fontSize: "0.4em" }}>%</span>
          </div>
          <div className={passed ? "stamp-in" : ""} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            {passed ? (
              // 修了 = 読了100% AND 合格（本棚のリング・学習管理と同じ定義）。合格しただけでは
              // 修了と言わない — 言うと管理側の記録と食い違う。
              isComplete(manualId) ? (
                <>
                  <Icon name="approval" size={22} style={{ color: "var(--ok)" }} />
                  <span className="display" style={{ fontSize: 26 }}>合格 — <span className="hl">修了</span></span>
                </>
              ) : (
                <>
                  <Icon name="approval" size={22} style={{ color: "var(--ok)" }} />
                  <span className="display" style={{ fontSize: 24 }}>合格！</span>
                </>
              )
            ) : (
              <span className="display" style={{ fontSize: 24, color: "var(--muted)" }}>不合格（{PASS_THRESHOLD}%以上で修了）</span>
            )}
          </div>
          <div className="mono" style={{ marginTop: 10, color: "var(--muted)" }}>{result.correct} / {result.total} 問正解</div>
        </div>

        {passed && !isComplete(manualId) ? (
          <Banner variant="info" icon={<Icon name="library" />} className="reveal">
            <span className="grow">まだ読んでいない手順があります。最後まで読むと<b>修了</b>になります。</span>
          </Banner>
        ) : null}

        {!passed && uniqWrong.length ? (
          <Banner variant="warn" icon={<Icon name="warning" />} className="reveal">
            <span className="grow">見直すと良い手順: {uniqWrong.map((s) => `手順${s}`).join("、")}</span>
          </Banner>
        ) : null}

        <div className="row" style={{ justifyContent: "center", flexWrap: "wrap", gap: 12, marginTop: 22 }}>
          <Btn variant="hi" lg onClick={() => void generate()} icon={<Icon name="redo-cycle" size={18} />}>もう一度挑戦</Btn>
          {!passed && uniqWrong.length ? (
            <Btn variant="ghost" lg onClick={() => onBackToStep(uniqWrong[0])} icon={<Icon name="arrow-left" size={18} />}>手順{uniqWrong[0]}を見直す</Btn>
          ) : (
            <Btn variant="ghost" lg onClick={onRestart} icon={<Icon name="arrow-left" size={18} />}>もう一度読む</Btn>
          )}
          <Btn variant="ghost" lg onClick={onClose}>書架に戻る</Btn>
        </div>
      </main>
    );
  }

  if (!questions) return <ReaderFrame><LoadingState /></ReaderFrame>;
  const q = questions[qi];
  const answeredThis = qi in answers;
  const last = qi === questions.length - 1;
  return (
    <main className="wrap" style={{ paddingTop: 24, paddingBottom: 60, maxWidth: 640 }}>
      <div className="spread" style={{ alignItems: "center", marginBottom: 14 }}>
        <div className="kicker">理解度クイズ</div>
        <Stepper total={questions.length} current={qi + 1} />
      </div>
      <QuizCard index={qi} question={q} selected={answers[qi]} onAnswer={answer} onReview={onBackToStep} />
      <div className="row" style={{ justifyContent: "space-between", marginTop: 18, gap: 12 }}>
        <Btn variant="ghost" lg disabled={qi === 0} onClick={() => setQi((i) => Math.max(0, i - 1))} icon={<Icon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />}>前へ</Btn>
        {last ? (
          <Btn variant="hi" lg disabled={!answeredThis} onClick={finish} icon={<Icon name="check" size={18} />}>結果を見る</Btn>
        ) : (
          <Btn variant="hi" lg disabled={!answeredThis} onClick={() => setQi((i) => Math.min(questions.length - 1, i + 1))}>
            次の問題<Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)", marginLeft: 6, verticalAlign: "middle" }} />
          </Btn>
        )}
      </div>
    </main>
  );
}
